/**
 * The settings you would change without reading code, in one place: `app.config.json`.
 *
 * JSON holds no comments, so the reasoning lives here — one paragraph per setting, next to the
 * type. The JSON stays something a person can open and edit; this file is what makes it typed and
 * gives the values one import site instead of six.
 *
 * It is read by BOTH halves of the plugin — the sandbox (`src/plugin/`) and the UI — so it must
 * stay plain data with no imports of its own. Every build inlines JSON at bundle time (Vite for
 * the UI, esbuild for the sandbox), so nothing is fetched at runtime and the sandbox bundle grows
 * by the bytes of the values themselves.
 *
 * What is deliberately NOT here:
 *
 *   - `functions/` constants (MAX_STORIES, MAX_URLS). That is a separate deployable — a Cloudflare
 *     Worker — with its own lifecycle. Reaching across into the plugin's config would couple two
 *     things that ship independently, and a worker reading a value the plugin was rebuilt with is
 *     worse than the duplication.
 *   - The API timeouts, endpoint paths and retry policy in `apiConfig.ts`. Those are already one
 *     module, they are tuning rather than deployment settings, and moving them buys nothing.
 */
// NAMED imports, not a default one. esbuild converts JSON into a module with one export per
// top-level key and tree-shakes the keys nobody imports — verified: a default import keeps every
// key, a named import drops the unused ones. That is what keeps the sandbox bundle from carrying
// `webAppUrl.development` (a localhost origin) into a production build.
import {
  webAppUrl as webAppUrlByEnvironment,
  apiBaseUrl as apiBaseUrlByEnvironment,
  apiEnvironment,
  turnstileSiteKey,
  window as pluginWindow,
  limits,
  timeouts,
} from './app.config.json';

/**
 * Injected by the bundlers' `define` — vite.config.ts for the UI, esbuild.plugin.mjs for the
 * sandbox. Declared HERE rather than in src/globals.d.ts because this module is compiled by
 * tsconfig.plugin.json too, which does not include that file; src/plugin/code.ts declares
 * `__WEB_APP_URL__` locally for the same reason. One declaration, visible to every project that
 * compiles this module.
 */
declare const __API_ENVIRONMENT__: string;

export interface AppConfig {
  /**
   * Where the plugin's iframe points.
   *
   * Read by esbuild.plugin.mjs at BUILD time and injected as `__WEB_APP_URL__`, so exactly one of
   * these two strings reaches the sandbox bundle. `src/plugin/` does not import it — doing that
   * would put a localhost origin inside a production build, which plugin/ui-origin.test.ts
   * refuses. Both origins still have to be listed in the manifest or the iframe is blocked; see
   * config/network-access.test.ts.
   */
  webAppUrl: { production: string; development: string };

  /**
   * Which deployment the plugin's API calls go to, keyed by `apiEnvironment`.
   *
   * Same-origin with the hosted UI in production: the API is Pages Functions served from the same
   * project under `/api`, which is what keeps the calls CORS-free and ships both halves together.
   */
  apiBaseUrl: { production: string; development: string; local: string };

  /** Which `apiBaseUrl` entry is live. Change this one value to point at a preview or a local `wrangler pages dev`. */
  apiEnvironment: 'production' | 'development' | 'local';

  /**
   * The PUBLIC Cloudflare Turnstile site key. Config, not a secret — it is readable in any page
   * that renders the widget, and the secret half lives only in `TURNSTILE_SECRET_KEY` on the
   * worker.
   *
   * Empty disables the client side, which is the right default rather than a placeholder: a key
   * that does not belong to this hostname fails the challenge for every real user, so an unset
   * one has to mean OFF and not BROKEN. The server reaches the same answer independently when no
   * secret is bound, and the two halves must agree or local development stops working — see
   * functions/_api/workers/lib/turnstile.ts.
   */
  turnstileSiteKey: string;

  /**
   * The plugin window.
   *
   * 720 rather than the original 650: that left the taller panels — Import completed with several
   * missing fonts, the batch URL list, Account settings — scrolling inside their own containers on
   * a laptop with room to spare. 720 still fits above the ~800px of usable height on a 13" display
   * at default scale, which is the smallest screen this has to sit on.
   */
  window: { width: number; height: number };

  limits: {
    /**
     * URLs in one batch import, and stories in one Storybook import.
     *
     * A client-side policy, not a server one — nothing in `functions/` rejects a longer list. It
     * was the same number written out in four places (CaptureService twice, BatchUrlInput's error
     * text, StorybookImport's MAX_SELECTED), so raising it meant finding all four and the ones you
     * missed disagreed silently: a list the input accepted, refused by the service that sent it.
     */
    batchUrls: number;

    /**
     * How many nodes a sandbox post-pass walks before giving up.
     *
     * A runaway guard and a real ceiling: a whole-site import puts every page in ONE section and
     * nothing caps the node count on the capture side. Reaching it is reported (`traversalTruncated`)
     * rather than silent — layers past the limit would otherwise keep generic names and miss
     * hyperlinks, HTML names and style binding, with nothing saying so.
     */
    traversalNodes: number;
  };

  timeouts: {
    /**
     * How long the UI waits for the sandbox's USER_INFO before saying it is not coming.
     *
     * The round trip is postMessage between an iframe and its parent, answered in milliseconds
     * when it is answered at all. Long enough that a slow first paint never trips it; short enough
     * that a broken relay is not mistaken for a slow one.
     */
    handshakeMs: number;
  };
}

/**
 * The individual settings. Import THESE rather than `appConfig` from anything the plugin sandbox
 * bundles — taking one key lets the others be shaken out, taking the whole object does not.
 */
export { apiEnvironment, turnstileSiteKey, pluginWindow, limits, timeouts };

/**
 * Everything at once, for the UI, where a few hundred bytes of unused config costs nothing and one
 * name reads better than six. Deliberately NOT used by `src/plugin/`.
 */
export const appConfig: AppConfig = {
  webAppUrl: webAppUrlByEnvironment,
  apiBaseUrl: apiBaseUrlByEnvironment,
  apiEnvironment,
  turnstileSiteKey,
  window: pluginWindow,
  limits,
  timeouts,
} as AppConfig;

/**
 * Which `apiBaseUrl` is live: the committed `apiEnvironment`, unless a DEPLOYMENT overrode it.
 *
 * The override exists because the committed value is global, and that made preview a lie. Its
 * worker, D1 and R2 are all separate, but its UI was built from the same config as production
 * and therefore called the PRODUCTION API — so a plugin pointed at preview showed preview's
 * frontend while writing to the live database. Isolated in every respect except the one that
 * matters.
 *
 * Overriding beats committing `development` to the preview branch: that branch gets merged into
 * main — twice in one day — and the value would ride along, leaving production serving the dev
 * database with nothing failing to announce it. The committed default stays `production`, so a
 * merge carries nothing and only the environment that sets the variable behaves differently.
 *
 * `__API_ENVIRONMENT__` is injected by the bundlers (vite.config.ts, esbuild.plugin.mjs) the way
 * `__BUILD_ID__` is, NOT read from `import.meta.env` here. Vite only substitutes the literal
 * text `import.meta.env.VITE_FOO`, so a cast or an intermediate variable leaves a dynamic lookup
 * that is undefined in the built bundle — the first attempt did exactly that and the override
 * silently never applied. `typeof` because the constant is absent under plain node (the tests).
 */
export function resolveApiEnvironment(): string {
  const override = typeof __API_ENVIRONMENT__ === 'string' ? __API_ENVIRONMENT__.trim() : '';
  if (!override) return apiEnvironment;
  if (override in apiBaseUrlByEnvironment) return override;
  // Not silent: an unrecognised value means someone intended a different API and did not get it,
  // which otherwise surfaces as data landing in the wrong database.
  console.error(
    `__API_ENVIRONMENT__="${override}" is not one of ` +
      `${Object.keys(apiBaseUrlByEnvironment).join(', ')}; using the committed "${apiEnvironment}".`,
  );
  return apiEnvironment;
}

/**
 * The API base URL for the live environment.
 *
 * THIS is the function that decides, which is worth saying because `apiConfig.ts` also had an
 * `ENVIRONMENT` constant that looked like it did — it only ever fed a log line. Putting the
 * override anywhere else changes what gets logged and nothing else.
 */
export function apiBaseUrl(): string {
  const env = resolveApiEnvironment();
  return (apiBaseUrlByEnvironment as Record<string, string>)[env] ?? apiBaseUrlByEnvironment.production;
}
